import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { FilterRenderer } from './FilterRenderer';
import { SortDropdown } from './SortDropdown';
import type { DynamicHeaderConfig } from '../../../contexts/DynamicHeaderContext';

interface HeaderFilterRowProps {
  config: DynamicHeaderConfig;
  menuCollapsed: boolean;
}

/**
 * Filtri "accesi" rispetto al punto di partenza. Esportata perche' la barra
 * filtri di mobile (LeadsPage) mostra lo stesso pulsante "Pulisci filtri" e
 * deve decidere allo stesso modo.
 */
export function hasActiveFilters(filtri: DynamicHeaderConfig['filters']): boolean {
  return !!filtri?.some(f => {
    if (f.id === 'sortOrder' || f.type === 'custom') return false;
    // Le scelte singole (es. "Solo Clienti") contano se sono diverse dal valore
    // di partenza: prima non contavano mai, quindi con una di quelle accesa il
    // pulsante "Pulisci filtri" non compariva nemmeno.
    if (f.type === 'select') {
      const iniziale = f.valoreIniziale ?? f.defaultValue;
      // Con un punto di partenza dichiarato (es. l'ordinamento) il filtro e'
      // acceso quando se ne discosta; senza, basta che abbia un valore. Prima
      // le scelte singole non contavano mai: su Ticket, Magazzino e Monitor
      // campagne si poteva filtrare senza vedere "Pulisci filtri".
      if (iniziale !== undefined) return f.value !== undefined && f.value !== iniziale;
      return f.value !== undefined && f.value !== null && f.value !== '' && f.value !== 'all';
    }
    if (f.type === 'date-range' || f.type === 'date-period') {
      // Il periodo puo' essere espresso come intervallo (da/a) oppure come anno
      // o trimestre: guardando solo da/a, con "Anno 2026" acceso il pulsante non
      // compariva e non c'era modo di togliere quel filtro in blocco.
      if (f.periodType === 'year') return true;
      if (f.periodType === 'quarter' && !!f.selectedQuarter) return true;
      return (f.dateFrom && f.dateFrom !== '') || (f.dateTo && f.dateTo !== '');
    }
    if (Array.isArray(f.value)) return f.value.length > 0;
    if (typeof f.value === 'boolean') return f.value === true;
    return f.value !== undefined && f.value !== null && f.value !== '';
  });
}

export function HeaderFilterRow({ config, menuCollapsed }: HeaderFilterRowProps) {
  const { isLight } = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreOnRight, setMoreOnRight] = useState(false);
  const [moreOnLeft, setMoreOnLeft] = useState(false);

  const updateOverflowHints = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreOnLeft(el.scrollLeft > 4);
    setMoreOnRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateOverflowHints();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateOverflowHints);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [updateOverflowHints, config.filters?.length]);

  // Con un mouse normale la rotella e' verticale: la si traduce in scorrimento
  // orizzontale, altrimenti i filtri fuori vista non si raggiungono.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      const max = el.scrollWidth - el.clientWidth;
      const goingRight = e.deltaY > 0;
      if ((goingRight && el.scrollLeft >= max - 1) || (!goingRight && el.scrollLeft <= 0)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [config.filters?.length]);

  const hasFilters = !!(config.filters && config.filters.length > 0);
  const hasRow = hasFilters || !!config.customFiltersRow;

  if (!hasRow) return null;
  if (config.filtersVisibleOnMobileOnly) return null;

  const isActive = hasActiveFilters(config.filters);

  return (
    <div
      className={`hidden sm:flex h-10 items-center pl-3 pr-3 gap-2 fixed z-[50] glass-menu overflow-visible ${
        isLight ? '' : 'border border-white/10 rounded-lg'
      }`}
      style={{
        top: isLight ? '3rem' : 'calc(env(safe-area-inset-top) + 0.5rem + 3.25rem)',
        left: isLight
          ? (menuCollapsed ? '4rem' : '16rem')
          : (menuCollapsed ? 'calc(4rem + 1rem)' : 'calc(16rem + 1rem)'),
        right: isLight ? '0' : '0.5rem',
      }}
    >
      {/* Scrollable filter area — solo i filtri.
          Senza `flex-1` questa area si dimensiona sul contenuto e si stringe
          (min-w-0) quando i filtri non entrano: così i controlli che seguono
          restano adiacenti ai filtri invece di essere spinti a destra. */}
      <div
        ref={scrollRef}
        onScroll={updateOverflowHints}
        className="flex items-center gap-2 overflow-x-auto min-w-0"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          ...((moreOnLeft || moreOnRight) ? {
            WebkitMaskImage: `linear-gradient(to right, ${moreOnLeft ? 'transparent, #000 24px' : '#000, #000'}, ${moreOnRight ? '#000 calc(100% - 24px), transparent' : '#000, #000'})`,
            maskImage: `linear-gradient(to right, ${moreOnLeft ? 'transparent, #000 24px' : '#000, #000'}, ${moreOnRight ? '#000 calc(100% - 24px), transparent' : '#000, #000'})`,
          } : {}),
        }}
      >
        {config.filters?.map(filter => (
          <div key={filter.id} className="flex-shrink-0">
            <FilterRenderer filter={filter} />
          </div>
        ))}
      </div>

      {/* Ordinamento e Pulisci filtri — fuori dall'area scrollabile.
          Dentro finivano oltre il bordo destro con molti filtri e la scrollbar
          è nascosta, quindi risultavano invisibili e irraggiungibili
          (segnalato sulla sezione Clienti il 30/07/2026). */}
      {config.sortOptions && config.sortOptions.length > 0 && (
        <div className="flex-shrink-0">
          <SortDropdown
            options={config.sortOptions}
            current={config.currentSort}
            onChange={config.onSortChange}
          />
        </div>
      )}

      {config.onClearFilters && isActive && (
        <button
          onClick={config.onClearFilters}
          className="flex-shrink-0 px-2.5 h-7 text-xs rounded-md text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors whitespace-nowrap"
        >
          Pulisci filtri
        </button>
      )}

      {/* Custom Filters Row — fuori dal container scrollabile per permettere dropdown.
          `ml-auto` ne conserva l'ancoraggio a destra ora che l'area filtri
          non è più `flex-1` (usato da MyTasksPage insieme a sortOptions). */}
      {config.customFiltersRow && (
        // Senza filtri standard (es. Dashboard) i controlli custom occupano la riga da sinistra
        <div className={hasFilters ? 'flex-shrink-0 ml-auto' : 'min-w-0 flex-1'}>
          {config.customFiltersRow}
        </div>
      )}
    </div>
  );
}
